/**
 * CSV Export Service
 *
 * Replaces the deprecated angular5-csv package with a native browser implementation.
 * Uses Blob and URL.createObjectURL for CSV generation and download.
 *
 * @example
 * ```typescript
 * import { CsvExportService, CsvExportOptions } from './csv-export.service';
 *
 * // Usage
 * CsvExportService.export(data, 'filename', {
 *   headers: ['Column 1', 'Column 2'],
 *   showLabels: true
 * });
 * ```
 */
export interface CsvExportOptions {
  /** Column headers for the CSV */
  headers?: string[];
  /** Whether to show headers as the first row */
  showLabels?: boolean;
  /** Field separator (default: ',') */
  separator?: string;
  /** Text qualifier for values containing separator (default: '"') */
  quoteChar?: string;
}

export class CsvExportService {
  /**
   * Export data to CSV and trigger browser download
   * @param data Array of objects or arrays to export
   * @param filename Name of the downloaded file (without .csv extension)
   * @param options Export configuration options
   */
  static export(
    data: Record<string, unknown>[] | unknown[][],
    filename: string,
    options: CsvExportOptions = {},
  ): void {
    const {
      headers,
      showLabels = true,
      separator = ',',
      quoteChar = '"',
    } = options;

    if (!data || data.length === 0) {
      console.warn('CsvExportService: No data to export');
      return;
    }

    const csvContent = this.generateCsv(data, {
      headers,
      showLabels,
      separator,
      quoteChar,
    });
    this.downloadCsv(csvContent, filename);
  }

  /**
   * Generate CSV string from data
   */
  private static generateCsv(
    data: Record<string, unknown>[] | unknown[][],
    options: Required<
      Pick<
        CsvExportOptions,
        'headers' | 'showLabels' | 'separator' | 'quoteChar'
      >
    >,
  ): string {
    const { headers, showLabels, separator, quoteChar } = options;
    const lines: string[] = [];

    // Determine if data is array of objects or array of arrays
    const isObjectArray = data.length > 0 && !Array.isArray(data[0]);

    // Handle headers
    if (showLabels && headers && headers.length > 0) {
      lines.push(
        headers
          .map((h) => this.escapeValue(h, separator, quoteChar))
          .join(separator),
      );
    } else if (showLabels && isObjectArray) {
      // Auto-generate headers from first object keys
      const firstRow = data[0] as Record<string, unknown>;
      const autoHeaders = Object.keys(firstRow);
      lines.push(
        autoHeaders
          .map((h) => this.escapeValue(h, separator, quoteChar))
          .join(separator),
      );
    }

    // Handle data rows
    for (const row of data) {
      if (Array.isArray(row)) {
        lines.push(
          row
            .map((cell) => this.escapeValue(cell, separator, quoteChar))
            .join(separator),
        );
      } else {
        const values = Object.values(row as Record<string, unknown>);
        lines.push(
          values
            .map((cell) => this.escapeValue(cell, separator, quoteChar))
            .join(separator),
        );
      }
    }

    return lines.join('\r\n');
  }

  /**
   * Escape a value for CSV format
   */
  private static escapeValue(
    value: unknown,
    separator: string,
    quoteChar: string,
  ): string {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = String(value);

    // Check if value needs quoting
    const needsQuoting =
      stringValue.includes(separator) ||
      stringValue.includes(quoteChar) ||
      stringValue.includes('\n') ||
      stringValue.includes('\r');

    if (needsQuoting) {
      // Escape quote characters by doubling them
      const escaped = stringValue.replace(
        new RegExp(quoteChar, 'g'),
        quoteChar + quoteChar,
      );
      return `${quoteChar}${escaped}${quoteChar}`;
    }

    return stringValue;
  }

  /**
   * Trigger browser download of CSV content
   */
  private static downloadCsv(content: string, filename: string): void {
    // Add BOM for Excel compatibility with UTF-8
    const bom = '\uFEFF';
    const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }
}
